import {
  Flex,
  Input,
  List,
  ListItem,
  PopoverBody,
  PopoverFooter,
  PopoverHeader,
  type useDisclosure,
} from '@chakra-ui/react';
import { type ChangeEventHandler, type Ref, useState } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { useEditMode } from '../../components/EditModeProvider/EditModeProvider';
import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import type { SecondaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import { ALL_FILTER_DEFINITIONS } from '../engine/filterRegistry';
import {
  FilterId,
  type FilterSelectOptions,
  FilterType,
} from '../types/filters';
import { AnimatedGradientBox } from './AnimatedGradientBox';
import { EditFiltersButton } from './EditFiltersButton';
import { FilterSelectFooter } from './FilterSelectFooter';
import styles from './FilterSelectList.module.css';
import { FilterSelectModal } from './FilterSelectModal';
import { AiSearchIcon } from './filterForms/talent-discovery/ai-search/AiSearchIcon';

interface FilterSelectListProps {
  filterOptions: FilterSelectOptions[];
  setSelectedFilter: (filter: FilterSelectOptions) => void;
  closeMenu: () => void;
  editFiltersModalDisclosure?: ReturnType<typeof useDisclosure>;
  searchInputRef?: Ref<HTMLInputElement>;
}

export const FilterSelectList = ({
  filterOptions,
  setSelectedFilter,
  closeMenu,
  editFiltersModalDisclosure,
  searchInputRef,
}: FilterSelectListProps) => {
  const [search, setSearch] = useState('');
  const [optionsStack, setOptionsStack] = useState<FilterSelectOptions[][]>([
    filterOptions,
  ]);
  const [filteredParentName, setFilteredParentName] = useState<string | null>(
    null
  );

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearch(e.target.value);
  };

  const currentOptions = optionsStack[optionsStack.length - 1] ?? [];

  const filteredFilterOptions = currentOptions?.filter((option) =>
    option.name.toLowerCase().includes(search.toLowerCase())
  );

  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );
  const handleFilterSelect = (filter: FilterSelectOptions) => {
    if (filter.id === 'AI_SEARCH') {
      setSelectedFilter(filter);
      return;
    }

    const filterId: FilterId = filter.id;
    const filterDefinition = ALL_FILTER_DEFINITIONS[filterId];

    if (filterDefinition?.type !== FilterType.GROUP) {
      setSelectedFilter(filter);
      return;
    }

    // Group filter clicked - show only its children
    const childOptions: FilterSelectOptions[] = (
      filterDefinition.children || []
    )
      .filter(
        (childId: FilterId) =>
          isValuationEnabled || childId !== FilterId.VALUATION
      )
      .map((childId: FilterId): FilterSelectOptions | null => {
        const childDef = ALL_FILTER_DEFINITIONS[childId];
        return childDef ? { id: childId, name: childDef.name } : null;
      })
      .filter((option): option is FilterSelectOptions => option !== null);

    setOptionsStack([...optionsStack, childOptions]);
    setFilteredParentName(filter.name);
    setSearch('');
  };

  const handleBack = () => {
    if (optionsStack.length > 1) {
      setOptionsStack(optionsStack.slice(0, optionsStack.length - 1));
      setFilteredParentName(null);
      setSearch('');
    }
  };

  const { config, dispatch } = useTabContentConfig();
  const { editMode } = useEditMode();
  const showEditFiltersInPopover = editMode;

  return (
    <>
      <PopoverHeader padding={0} borderBottomWidth={0}>
        <Input
          ref={searchInputRef}
          placeholder={
            filteredParentName
              ? `${filteredParentName} filters`
              : 'Select a filter'
          }
          size="sm"
          onChange={handleSearch}
          value={search}
          className={styles.searchInput}
        />
      </PopoverHeader>
      <PopoverBody padding={0} data-testid="filter-menu-popover">
        <List pb={2} className={styles.list}>
          {filteredFilterOptions.length === 0 && (
            <ListItem paddingY={2} textAlign="center" opacity={0.5}>
              No options
            </ListItem>
          )}

          {filteredFilterOptions.map((option) => {
            const isAISearch = option.id === 'AI_SEARCH';
            return (
              <AnimatedGradientBox key={option.id} active={isAISearch}>
                <ListItem
                  role="button"
                  cursor="pointer"
                  onClick={() => handleFilterSelect(option)}
                  paddingX={3}
                  paddingY={1.5}
                  className={isAISearch ? undefined : styles.listItem}
                  {...(isAISearch && { _hover: { bg: '#9AF2A833' } })}
                  data-testid="select-filter-option"
                >
                  <Flex alignItems="center" gap={2}>
                    {isAISearch && <AiSearchIcon />}
                    {option.name}
                  </Flex>
                </ListItem>
              </AnimatedGradientBox>
            );
          })}
        </List>
      </PopoverBody>
      <PopoverFooter
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="8px 0 0"
        borderTopWidth={0}
      >
        {editFiltersModalDisclosure && (
          <EditFiltersButton onOpenModal={editFiltersModalDisclosure.onOpen} />
        )}
        <Flex flex="1">
          <FilterSelectFooter
            onClose={closeMenu}
            onBack={optionsStack.length > 1 ? handleBack : undefined}
            isAddDisabled
          />
        </Flex>
      </PopoverFooter>
      {showEditFiltersInPopover && editFiltersModalDisclosure && (
        <FilterSelectModal
          isOpen={editFiltersModalDisclosure.isOpen}
          onClose={editFiltersModalDisclosure.onClose}
          currentFilters={config.secondaryFilters}
          onSave={(filters: SecondaryFilterConfig[]) => {
            dispatch({
              type: 'SET_SECONDARY_FILTERS',
              payload: { filters },
            });
          }}
        />
      )}
    </>
  );
};
